import { useCallback, useEffect, useRef, useState } from "react";

export const useDraggable = ({
  size = 76,
  margin = 8,
  boundary,
}: UseDraggableOptions = {}): UseDraggableResult => {
  const defaultBottom = boundary?.bottom ?? 16;
  const [position, setPosition] = useState<DraggablePosition>({
    mode: "anchor",
    bottom: defaultBottom,
    right: 14,
  });
  const [isDragging, setIsDragging] = useState(false);

  const dragState = useRef({
    startX: 0,
    startY: 0,
    startPosX: 0,
    startPosY: 0,
    dragging: false,
    thresholdExceeded: false,
  });

  const clampPosition = useCallback(
    (x: number, y: number): AbsolutePosition => {
      const maxX = window.innerWidth - size - margin;
      const minY = boundary?.top || 0;
      const maxY = window.innerHeight - size - (boundary?.bottom || 0);
      return {
        mode: "absolute",
        x: clamp(x, margin, maxX),
        y: clamp(y, minY, maxY),
      };
    },
    [size, margin, boundary?.top, boundary?.bottom]
  );

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      const state = dragState.current;
      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;

      if (!state.thresholdExceeded) {
        if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
          state.thresholdExceeded = true;
          state.dragging = true;
          setIsDragging(true);
        } else {
          return;
        }
      }

      const newPos = clampPosition(state.startPosX + dx, state.startPosY + dy);
      setPosition(newPos as DraggablePosition);
    },
    [clampPosition]
  );

  const onPointerUp = useCallback(() => {
    const state = dragState.current;
    state.dragging = false;
    setIsDragging(false);

    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
  }, [onPointerMove]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const state = dragState.current;
      state.startX = e.clientX;
      state.startY = e.clientY;
      // Convert anchor position to absolute coordinates for dragging
      if (position.mode === "anchor") {
        state.startPosX = window.innerWidth - size - position.right;
        state.startPosY = window.innerHeight - size - defaultBottom;
      } else {
        state.startPosX = position.x;
        state.startPosY = position.y;
      }
      state.dragging = false;
      state.thresholdExceeded = false;

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
    },
    [position, onPointerMove, onPointerUp]
  );

  // Clamp position when the viewport resizes
  useEffect(() => {
    const onResize = () => {
      setPosition((pos) => {
        if (pos.mode === "anchor") return pos;
        return clampPosition(pos.x, pos.y);
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clampPosition]);

  return {
    position,
    isDragging,
    wasDragged: () => dragState.current.thresholdExceeded,
    dragHandlers: { onPointerDown },
  };
};

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

interface AnchorPosition {
  mode: "anchor";
  bottom: number;
  right: number;
}

interface AbsolutePosition {
  mode: "absolute";
  x: number;
  y: number;
}

const DRAG_THRESHOLD = 5;

interface Boundary {
  bottom?: number;
  top?: number;
}

interface UseDraggableOptions {
  /** Element size for clamping (width and height) */
  size?: number;
  /** Margin from viewport edges */
  margin?: number;
  /** Boundary constraints: bottom sets default anchor and min-y clamp, top sets max-y clamp */
  boundary?: Boundary;
}

type DraggablePosition = AnchorPosition | AbsolutePosition;

interface UseDraggableResult {
  position: DraggablePosition;
  isDragging: boolean;
  /** Returns true if a drag threshold was exceeded — safe to call in onClick handlers */
  wasDragged: () => boolean;
  dragHandlers: {
    onPointerDown: (e: React.PointerEvent) => void;
  };
}
